import { LINE_BLOCK_NOTICES, LINE_LEVEL_CURRENT_LABEL, LINE_LEVEL_STATE_COPY, lineHomeBannerCopy, LineNoticeCopy } from '../constants/credit-line.constants';
import { CreditLine, CreditLineLevelSpec, CreditLineMark, CreditLineResponse, Credito, LinePresentation } from '../models';
import { CreditAccessStatus, CreditosService } from '../services/creditos.service';
import { resolveCreditLineView } from './credit-line-view.util';
import { formatLineUsd } from './line-format.util';

export type { LinePresentation } from '../models';

/** La línea quedó apagada por castigo. Se lee `status` y `blockReason` en doble, como el servidor. */
export function isLineWrittenOff(line: Pick<CreditLine, 'status' | 'blockReason'> | null | undefined): boolean {
    return line?.status === 'written_off' || line?.blockReason === 'written_off';
}

/**
 * La marca que ve el comercio (DT-30): con la línea castigada no se muestra ninguna, porque el
 * castigo no se levanta y la línea no vuelve a encenderse, aunque `GET /line` siga trayendo `mark`.
 * Única fuente para la alerta de marca, la fila de «Cómo llegas» y la nota del modal de préstamo pagado.
 */
export function visibleLineMark(line: Pick<CreditLine, 'status' | 'blockReason' | 'mark'> | null | undefined): CreditLineMark | null {
    if (!line || isLineWrittenOff(line)) return null;
    return line.mark ?? null;
}

/**
 * Presentación de la línea v2 (FINA-3378, diseño Fina Presta). Compone la vista del crédito
 * (`resolveCreditLineView`, la única fuente de `line` / `in_review` / `loan`) con el estado de la
 * línea. La usan la franja, Niveles y (en `presta-home`) el banner del Home: ninguno vuelve a
 * leer `credito` ni `line` para decidir el copy.
 *
 * Orden de resolución (se lee `status` y `blockReason` en doble, como el servidor):
 *   1. `written_off` → `off`
 *   2. crédito `requested` → `in_review`
 *   3. crédito `active` / `late` → `in_loan` con su monto
 *   4. `open_credit` sin crédito abierto visible → `in_loan` sin monto
 *   5. `suspended` → `paused`
 *   6. `not_eligible` → `no_access`
 *   7. `below_floor` → `below_floor`
 *   8. `recalc_pending` → `recalc_pending` (recálculo abierto; se pide cuando soporte responda)
 *   9. `daily_limit` → `paused_today` (el día agotó el tope de Fina; mañana se puede pedir)
 *  10. `canRequest` → `ready`
 *  11. cualquier otro caso (incluido un `blockReason` nuevo del servidor) → `no_access`
 * `recalc_pending` y `daily_limit` nunca llegan juntos: su orden es el del servidor.
 */
export function resolveLinePresentation(line: CreditLine | null | undefined, credito: Pick<Credito, 'status' | 'amount'> | null | undefined): LinePresentation {
    if (!line) return { kind: 'no_access' };
    if (isLineWrittenOff(line)) return { kind: 'off' };

    const view = resolveCreditLineView(credito);
    if (view === 'in_review') return { kind: 'in_review', amountUSD: Number(credito?.amount) || 0 };
    if (view === 'loan') {
        const amount = Number(credito?.amount);
        return { kind: 'in_loan', amountUSD: credito?.amount != null && Number.isFinite(amount) ? amount : null };
    }
    if (line.blockReason === 'open_credit') return { kind: 'in_loan', amountUSD: null };

    if (line.status === 'suspended' || line.blockReason === 'suspended') {
        return { kind: 'paused', daysWithoutSales: line.access?.activity?.daysSinceLastSale ?? null, cupoUSD: Number(line.cupo?.amountUSD) || 0 };
    }
    if (line.status === 'not_eligible' || line.blockReason === 'not_eligible') return { kind: 'no_access', cupoUSD: Number(line.cupo?.amountUSD) || 0 };
    if (line.blockReason === 'below_floor') return { kind: 'below_floor' };
    if (line.blockReason === 'recalc_pending') return { kind: 'recalc_pending' };
    if (line.blockReason === 'daily_limit') return { kind: 'paused_today' };
    if (line.canRequest === true) return { kind: 'ready', cupoUSD: Number(line.cupo?.amountUSD) || 0, level: line.level, closedCredits: Number(line.closedCredits) || 0 };
    return { kind: 'no_access' };
}

/**
 * Banner de Fina Presta en el Home del dueño (presta-home). Devuelve la presentación solo si se
 * cumplen todas las condiciones; si no, `null` (sin banner, nunca cae a `ready`):
 *   1. la línea es `v2` con `status` (la forma del no dueño, `{ version: 'v2' }`, no lo trae);
 *   2. el estado de acceso de la visita cargó (`null` = pendiente o falló);
 *   3. el crédito no está en mora: en mora el Home muestra solo `fina-credit-due-banner`;
 *   4. el banner de la cuota no está visible (pre-vencimiento o bloqueo, `isDueBannerVisible`);
 *   5. ese `kind` tiene copy en el Home (`lineHomeBannerCopy`).
 * `credito` es `null` cuando `/current` no trae crédito (o está borrado).
 */
export function resolveHomeLinePresentation(data: CreditLineResponse['data'] | null | undefined, credito: Credito | null, access: CreditAccessStatus | null): LinePresentation | null {
    const line = CreditosService.lineOf(data);
    if (!line?.status || !access) return null;
    if (CreditosService.hasLateInstallment(credito) || CreditosService.isDueBannerVisible(access)) return null;
    const presentation = resolveLinePresentation(line, credito);
    return lineHomeBannerCopy(presentation) ? presentation : null;
}

/**
 * Por qué no se puede pedir: la ÚNICA fuente del motivo de bloqueo (card de solicitud, pie de la
 * card del nivel actual y modal de préstamo pagado). Manda `status` y, dentro de `active`, el
 * `blockReason`, con la prioridad del servidor. `not_eligible` se completa con el checklist.
 * Apagada y en pausa usan el copy literal del diseño, sin monto/fecha del castigo ni días de venta.
 */
export function resolveLineNotice(line: CreditLine | null | undefined): LineNoticeCopy | null {
    if (!line || line.canRequest) return null;
    if (isLineWrittenOff(line)) return LINE_BLOCK_NOTICES.written_off;
    if (line.status === 'not_eligible' || line.blockReason === 'not_eligible') return LINE_BLOCK_NOTICES.not_eligible;
    if (line.status === 'suspended' || line.blockReason === 'suspended') return LINE_BLOCK_NOTICES.suspended;
    const reason = line.blockReason;
    return (reason && LINE_BLOCK_NOTICES[reason]) || LINE_BLOCK_NOTICES.unknown;
}

export interface LevelRequirementProgress {
    have: number;
    need: number;
    missing: number;
    /** Avance 0–100. Sin requisito, completo. */
    pct: number;
}

export interface LevelRequirements {
    closed: LevelRequirementProgress;
    points: LevelRequirementProgress;
    /** Sin marca activa: el motor no sube de nivel con una marca. */
    noMark: boolean;
    /** Créditos, puntos y sin marca: lo que el motor exige para subir. */
    allMet: boolean;
}

const progress = (have: number | null | undefined, need: number | null | undefined): LevelRequirementProgress => {
    const h = Math.max(0, Number(have) || 0);
    const n = Math.max(0, Number(need) || 0);
    return { have: h, need: n, missing: Math.max(0, n - h), pct: n <= 0 ? 100 : Math.max(0, Math.min(100, Math.round((h / n) * 100))) };
};

/**
 * Cuánto lleva el comercio hacia un nivel, con lo que decide el motor (decisión 5): créditos
 * pagados, puntos y sin marca activa. Actividad y suscripción no son requisitos del nivel.
 */
export function levelRequirements(line: Pick<CreditLine, 'closedCredits' | 'points' | 'mark'>, spec: Pick<CreditLineLevelSpec, 'closedCreditsRequired' | 'pointsRequired'>): LevelRequirements {
    const closed = progress(line?.closedCredits, spec?.closedCreditsRequired);
    const points = progress(line?.points, spec?.pointsRequired);
    const noMark = !line?.mark;
    return { closed, points, noMark, allMet: closed.missing === 0 && points.missing === 0 && noMark };
}

/** Tasa de respaldo si la línea no trae `terms` (la base del motor de `api`, `BASE_INTEREST_RATE`). */
export const LINE_DEFAULT_INTEREST_RATE = 0.1;

/**
 * Cuántos préstamos más a tiempo le faltan al comercio para el siguiente nivel (modal de préstamo
 * pagado): `máx(créditos faltantes, ⌈puntos faltantes / puntos por préstamo⌉)`.
 *
 * Puntos por préstamo = cupo × (1 + tasa del nivel actual): el motor suma por cuota su monto
 * con interés × `TYPE_MULTIPLIER` (`api/src/modules/credit-line/engine/replay.ts:182`,
 * `creditLine.constants.ts:55`: A 1,5 · B 1 · C 0,5). Se asume pago tipo B (×1) y préstamo al
 * cupo actual, así que K es un MÁXIMO conservador: pagando adelantado (tipo A) bastan menos.
 * Deuda conocida: que `api` exponga este número en `GET /line`. Sin siguiente nivel, 0.
 */
export function loansMissingForNext(line: Pick<CreditLine, 'closedCredits' | 'points' | 'mark' | 'nextLevel' | 'cupo' | 'terms'> | null | undefined): number {
    const next = line?.nextLevel;
    if (!line || !next) return 0;
    const req = levelRequirements(line, next);
    const cupo = Number(line.cupo?.amountUSD) || 0;
    const rate = Number(line.terms?.interestRate) || LINE_DEFAULT_INTEREST_RATE;
    const byPoints = req.points.missing <= 0 ? 0 : cupo > 0 ? Math.ceil(req.points.missing / (cupo * (1 + rate))) : 1;
    return Math.max(req.closed.missing, byPoints);
}

export type LevelCardStateKind = 'done' | 'current' | 'next' | 'locked';

export interface LevelCardState {
    state: LevelCardStateKind;
    /** Pill de la card. */
    label: string;
    /** Subtítulo bajo «Nivel N». */
    subtitle: string;
}

/**
 * Estado de la card de un nivel en «Niveles y beneficios» (como el prototipo): superado si está
 * por debajo del nivel actual, actual, siguiente (`level + 1`) o bloqueado. La pill del actual
 * siempre dice «Activo»: cómo está la línea lo dice el pie (`lineLevelFooterCopy`).
 */
export function levelCardState(level: number, line: Pick<CreditLine, 'level'>): LevelCardState {
    const current = Number(line?.level) || 0;
    if (level < current) return { state: 'done', ...LINE_LEVEL_STATE_COPY.done };
    if (level === current) return { state: 'current', label: LINE_LEVEL_CURRENT_LABEL, subtitle: LINE_LEVEL_STATE_COPY.current.subtitle };
    if (level === current + 1) return { state: 'next', ...LINE_LEVEL_STATE_COPY.next };
    return { state: 'locked', ...LINE_LEVEL_STATE_COPY.locked };
}

export interface LineLadderStep {
    level: number;
    /** Tope del nivel, ya formateado («$1.250»). */
    cap: string;
    installmentsCount: number;
    /** Es el nivel actual de la línea. */
    current: boolean;
}

/**
 * Escalera de niveles de la bienvenida y del modal de préstamo pagado: un paso por elemento de
 * `line.levels` (la escalera de api), en orden. Sin `levels` devuelve `[]` y la escalera no se pinta.
 */
export function lineLadderSteps(line: Pick<CreditLine, 'level' | 'levels'> | null | undefined): LineLadderStep[] {
    const levels = line?.levels ?? [];
    return levels.map(spec => ({
        level: spec.level,
        cap: `$${formatLineUsd(spec.capUSD)}`,
        installmentsCount: Number(spec.terms?.installmentsCount) || 0,
        current: spec.level === line?.level,
    }));
}
